/**
 * LetterAssistantDialog — Assistant rédaction de lettres juridiques
 * pré-remplies (préavis, non-reconduction, résiliation anticipée…)
 * avec édition inline et export PDF + DOCX.
 */
import { useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Loader2, FileText, Download, RefreshCw, Send, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const KIND_OPTS = [
  { value: "non_reconduction", label: "Non-reconduction tacite" },
  { value: "preavis", label: "Préavis de fin de contrat" },
  { value: "resiliation_anticipee", label: "Résiliation anticipée (pour motif)" },
  { value: "modification_tarifaire", label: "Demande de modification tarifaire" },
];

export default function LetterAssistantDialog({ open, onOpenChange, contract }) {
  const [kind, setKind] = useState("non_reconduction");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [content, setContent] = useState("");

  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/draft-letter`, {
        kind, extra_context: extra,
      });
      setContent(data.content);
      toast.success("Brouillon généré");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (fmt) => {
    if (!content.trim()) { toast.error("Génère d'abord la lettre"); return; }
    setDownloading(fmt);
    try {
      const r = await api.post(`/contracts/${contract.id}/letter/download`,
        { content, fmt, kind },
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], {
        type: fmt === "pdf" ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const kindLabel = (KIND_OPTS.find((k) => k.value === kind) || {}).label || "Lettre";
      a.href = url;
      a.download = `${kindLabel} — ${contract.title || contract.party_name || "Contrat"}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloading(""); }
  };

  const copy = async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copié dans le presse-papiers");
    } catch { toast.error("Copie impossible"); }
  };

  const reset = () => { setContent(""); setExtra(""); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0"
                     data-testid="letter-assistant-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-lg flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </div>
            Assistant rédaction de lettre
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          <div className="text-xs text-slate-500 mt-0.5">
            L'IA rédige une lettre juridiquement solide, pré-remplie avec les
            données du contrat. Édite-la puis télécharge en PDF ou DOCX.
          </div>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr] overflow-hidden">
          {/* Sidebar */}
          <div className="border-r bg-slate-50/50 p-4 space-y-4 overflow-y-auto">
            <div>
              <Label className="text-xs font-medium">Type de lettre</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-9 mt-1 text-sm bg-white"
                                data-testid="letter-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-medium">
                Contexte additionnel <span className="text-slate-400">(optionnel)</span>
              </Label>
              <Textarea value={extra} onChange={(e) => setExtra(e.target.value)}
                        rows={4} className="mt-1 text-xs bg-white"
                        placeholder="Ex : motif exact, difficulté rencontrée, référence LRAR précédente…"
                        data-testid="letter-extra" />
            </div>

            <Button onClick={draft} disabled={busy}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="letter-draft-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : content ? <RefreshCw className="h-4 w-4 mr-1.5" />
                    : <Sparkles className="h-4 w-4 mr-1.5" />}
              {content ? "Régénérer" : "Générer la lettre"}
            </Button>

            {content && (
              <div className="space-y-1.5">
                <Button onClick={() => download("pdf")} disabled={!!downloading}
                        variant="outline" className="w-full h-9"
                        data-testid="letter-download-pdf">
                  {downloading === "pdf"
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5 mr-1.5" />}
                  Télécharger PDF
                </Button>
                <Button onClick={() => download("docx")} disabled={!!downloading}
                        variant="outline" className="w-full h-9"
                        data-testid="letter-download-docx">
                  {downloading === "docx"
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                  Télécharger DOCX
                </Button>
                <Button onClick={copy} variant="ghost" size="sm" className="w-full h-8 text-xs">
                  <Copy className="h-3 w-3 mr-1.5" /> Copier
                </Button>
              </div>
            )}

            <div className="text-[11px] text-slate-500 border-t pt-3">
              <b>💡 Astuce :</b> Modifie librement le texte à droite avant de
              télécharger. Le PDF inclut la mise en page A4 aux normes courrier.
            </div>
          </div>

          {/* Preview / edit */}
          <div className="p-5 flex flex-col overflow-hidden">
            {!content && !busy && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-sm text-slate-400 max-w-sm">
                  <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  Choisis un type de lettre puis clique sur
                  <b className="text-slate-600"> Générer la lettre</b>.
                  L'IA compose une lettre juridiquement solide en 10 s.
                </div>
              </div>
            )}
            {busy && !content && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 mx-auto mb-3 text-emerald-500 animate-spin" />
                  <div className="text-sm text-slate-600">
                    L'IA rédige la lettre — quelques secondes…
                  </div>
                </div>
              </div>
            )}
            {content && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    Aperçu (éditable)
                  </div>
                  <button onClick={reset} className="text-xs text-slate-500 hover:text-red-600">
                    Recommencer
                  </button>
                </div>
                <Textarea value={content}
                          onChange={(e) => setContent(e.target.value)}
                          className="flex-1 font-mono text-xs leading-relaxed resize-none bg-white"
                          data-testid="letter-content" />
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
